import { Controller } from 'stimulus'

const NEXT_DISABLED_TOOLTIP = 'Please answer this question to continue'

export default class extends Controller {
  static targets = ['step', 'panel', 'actions', 'prevButton', 'nextButton', 'nextWrap', 'startButton']

  connect() {
    this.currentIndex = 0
    this.boundSyncNextButtonState = this.syncNextButtonState.bind(this)
    this.element.addEventListener('change', this.boundSyncNextButtonState)
    this.element.addEventListener('input', this.boundSyncNextButtonState)
    this.showStep(this.currentIndex)
  }

  disconnect() {
    this.element.removeEventListener('change', this.boundSyncNextButtonState)
    this.element.removeEventListener('input', this.boundSyncNextButtonState)
    this.clearNextTooltip()
  }

  next(event) {
    if (event) event.preventDefault()

    if (this.currentIndex > 0 && !this.isCurrentStepComplete()) {
      return
    }

    if (this.currentIndex >= this.stepTargets.length - 1) {
      const form = this.element.querySelector('form')
      if (form) form.requestSubmit ? form.requestSubmit() : form.submit()
      return
    }

    this.currentIndex += 1
    this.showStep(this.currentIndex)
  }

  previous(event) {
    if (event) event.preventDefault()
    if (this.currentIndex <= 0) return

    this.currentIndex -= 1
    this.showStep(this.currentIndex)
  }

  showStep(index) {
    this.stepTargets.forEach((step, i) => {
      step.hidden = i !== index
      step.classList.toggle('is-active', i === index)
    })

    const onWelcome = index === 0
    const onLast = index === this.stepTargets.length - 1

    if (this.hasPanelTarget) {
      this.panelTarget.hidden = onWelcome
    }

    if (this.hasActionsTarget) {
      this.actionsTarget.hidden = onWelcome
    }

    if (this.hasPrevButtonTarget) {
      this.prevButtonTarget.hidden = onWelcome
    }

    if (this.hasNextButtonTarget) {
      this.setNextButtonLabel(onLast ? 'Submit feedback' : 'Continue')
    }

    this.syncNextButtonState()

    if (!onWelcome) {
      window.scrollTo(0, 0)
    }
  }

  setNextButtonLabel(label) {
    const button = this.nextButtonTarget
    let icon = button.querySelector('i')
    if (!icon) {
      icon = document.createElement('i')
      icon.className = 'fa fa-arrow-right'
      icon.setAttribute('aria-hidden', 'true')
    }

    button.textContent = ''
    button.appendChild(document.createTextNode(label + ' '))
    button.appendChild(icon)
  }

  syncNextButtonState() {
    if (!this.hasNextButtonTarget) return

    const incomplete = this.currentIndex > 0 && !this.isCurrentStepComplete()
    this.nextButtonTarget.disabled = incomplete
    this.nextButtonTarget.setAttribute('aria-disabled', incomplete ? 'true' : 'false')

    if (incomplete) {
      this.showNextTooltip()
    } else {
      this.clearNextTooltip()
    }
  }

  showNextTooltip() {
    if (!this.hasNextWrapTarget) return

    this.nextWrapTarget.setAttribute('title', NEXT_DISABLED_TOOLTIP)
    this.nextWrapTarget.setAttribute('data-bs-toggle', 'tooltip')
    this.nextWrapTarget.setAttribute('data-bs-placement', 'top')

    if (window.$) {
      const $wrap = window.$(this.nextWrapTarget)
      $wrap.tooltip('dispose')
      $wrap.tooltip({ trigger: 'hover focus', container: 'body' })
    }
  }

  clearNextTooltip() {
    if (!this.hasNextWrapTarget) return

    if (window.$) {
      window.$(this.nextWrapTarget).tooltip('dispose')
    }

    this.nextWrapTarget.removeAttribute('title')
    this.nextWrapTarget.removeAttribute('data-bs-toggle')
    this.nextWrapTarget.removeAttribute('data-bs-placement')
  }

  isCurrentStepComplete() {
    const step = this.stepTargets[this.currentIndex]
    if (!step) return true

    const answers = step.querySelector('[data-target~="survey-taker.answers"]') || step
    const fields = Array.from(answers.querySelectorAll('input:not([type="hidden"]), select, textarea'))
    if (fields.length === 0) return true

    const checkboxes = fields.filter((field) => field.type === 'checkbox')
    const radios = fields.filter((field) => field.type === 'radio')
    const textareas = fields.filter((field) => field.tagName === 'TEXTAREA')

    if (checkboxes.length > 0 || textareas.length > 0) {
      // Checkboxes and comment boxes are optional.
      return true
    }

    if (radios.length > 0) {
      const required = radios.some((radio) => radio.required)
      return !required || radios.some((radio) => radio.checked)
    }

    return fields[0].checkValidity()
  }
}
